import React from 'react';
import {
  ST_DOCTORS,
  ST_OFFICE_HOURS,
  ST_PRACTICE_PROFILE_FIELDS,
  ST_SERVICES,
} from '../../growth-settings/mockData';
import type { StDoctorItem, StPracticeProfileFieldId } from '../../growth-settings/types';
import {
  StAddChip,
  StChip,
  StFieldHeading,
  StFlatPanel,
  StSectionCard,
  type StSectionSaveFooter,
} from './SettingsPrimitives';

export function SettingsPracticeProfile({
  values,
  onChange,
  doctors = ST_DOCTORS,
  saveFooter,
}: {
  values: Record<StPracticeProfileFieldId, string>;
  onChange: (id: StPracticeProfileFieldId, value: string) => void;
  doctors?: StDoctorItem[];
  saveFooter?: StSectionSaveFooter;
}) {
  return (
    <StSectionCard
      title="Practice Profile"
      description="ELVA uses this information to write your ads, landing pages, and articles. Keep it up to date."
      {...(saveFooter ? { saveFooter } : {})}
    >
      <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {ST_PRACTICE_PROFILE_FIELDS.map((field) => (
          <label key={field.id} className="block">
            <span className="text-[13.5px] font-medium text-[var(--ink-1)]">{field.label}</span>
            <input
              value={values[field.id]}
              onChange={(event) => onChange(field.id, event.target.value)}
              className="mt-2 h-11 w-full rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 text-[15px] text-[var(--ink-1)] outline-none focus:border-[var(--ink-4)]"
            />
          </label>
        ))}
      </section>

      <section>
        <StFieldHeading
          title="Services You Offer"
          sub="ELVA will only suggest campaigns for services you offer."
        />
        <div className="mt-3 flex flex-wrap gap-2">
          {ST_SERVICES.map((service) => (
            <StChip key={service}>{service}</StChip>
          ))}
          <StAddChip label="+ Add Service" />
        </div>
      </section>

      <section>
        <StFieldHeading
          title="Doctors & Staff"
          sub="Doctors featured in ads and landing pages."
        />
        <div className="mt-3 space-y-2">
          {doctors.map((doctor) => (
            <StFlatPanel key={doctor.id}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[15px] font-semibold text-[var(--ink-1)]">{doctor.name}</p>
                  <p className="mt-0.5 text-[13px] text-[var(--ink-3)]">{doctor.roleLine}</p>
                </div>
                <button
                  type="button"
                  className="inline-flex h-8 cursor-pointer items-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 text-[13px] font-medium text-[var(--ink-2)]"
                >
                  Edit
                </button>
              </div>
            </StFlatPanel>
          ))}
          <button
            type="button"
            className="flex h-11 w-full cursor-pointer items-center justify-center rounded-[var(--r-md)] border border-dashed border-[var(--ink-4)] bg-transparent px-3 text-[15px] font-medium text-[var(--ink-3)]"
          >
            + Add Doctor or Staff
          </button>
        </div>
      </section>

      <section>
        <StFieldHeading title="Office Hours" />
        <div
          className="gs-officehours mt-3 grid gap-2"
          style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}
        >
          {ST_OFFICE_HOURS.map((item) => (
            <div
              key={item.day}
              className="rounded-[var(--r-md)] border px-3 py-3 text-center"
              style={
                item.closed
                  ? { borderColor: 'var(--cream-2)', background: 'var(--surface-mute)' }
                  : {
                      borderColor: 'color-mix(in srgb, var(--success) 35%, transparent)',
                      background: 'var(--success-tint)',
                    }
              }
            >
              <p
                className="text-[13px] font-semibold"
                style={{ color: item.closed ? 'var(--ink-3)' : 'var(--success)' }}
              >
                {item.day}
              </p>
              <p
                className="mt-1 text-[13px]"
                style={{ color: item.closed ? 'var(--ink-3)' : 'var(--success)' }}
              >
                {item.hour}
              </p>
            </div>
          ))}
        </div>
      </section>
    </StSectionCard>
  );
}
